import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Sparkles, ArrowRight, ArrowLeft, Mic, Square, Shuffle } from "lucide-react";
import api from "../api";
import { Btn, Spinner } from "../ui";

const CASE_TYPES = ["career", "business", "finance", "family", "relationship", "health", "legal", "education", "other"];

function blobToBase64(blob) {
  return new Promise((resolve) => {
    const r = new FileReader();
    r.onloadend = () => resolve(r.result.split(",")[1]);
    r.readAsDataURL(blob);
  });
}

export default function NewCase() {
  const [step, setStep] = useState(1);
  const [intents, setIntents] = useState([]);
  const [advisor, setAdvisor] = useState(null); // null | "auto" | advisor object
  const [situation, setSituation] = useState("");
  const [caseType, setCaseType] = useState("");
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const recRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/advisors").then((r) => setIntents(r.data.intents || [])).catch(() => {});
  }, []);

  const pickIntent = async (intent) => {
    try {
      const r = await api.post("/advisors/recommend", { intent_id: intent.id });
      setAdvisor(r.data.advisor);
      setStep(2);
    } catch {
      toast.error("Could not recommend an advisor.");
    }
  };

  const pickAuto = () => {
    setAdvisor("auto");
    setStep(3);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      const chunks = [];
      rec.ondataavailable = (e) => chunks.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks, { type: rec.mimeType || "audio/webm" });
        const b64 = await blobToBase64(blob);
        try {
          const r = await api.post("/audio/transcribe", { audio_base64: b64, mime: blob.type });
          if (r.data.text) setSituation((s) => (s ? `${s} ${r.data.text}` : r.data.text));
        } catch {
          toast.error("Transcription failed — type it instead.");
        }
      };
      rec.start();
      recRef.current = rec;
      setRecording(true);
    } catch {
      toast.error("Microphone unavailable.");
    }
  };

  const stopRecording = () => {
    recRef.current?.stop();
    setRecording(false);
  };

  const submit = async () => {
    if (situation.trim().length < 3) return;
    setBusy(true);
    try {
      const r = await api.post("/cases", {
        situation: situation.trim(),
        case_type: caseType || undefined,
        advisor_id: advisor === "auto" ? "auto" : advisor?.id,
        working_title: title.trim() || undefined,
      });
      navigate(`/app/case/${r.data.id}?tab=interview`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not open the case.");
      setBusy(false);
    }
  };

  return (
    <div data-testid="new-case-page" className="max-w-3xl">
      <p className="font-mono-label text-[10px] tracking-[0.3em] uppercase text-[#B5A58A]/80 mb-4">
        Open a new case — Step {step} of 3
      </p>

      {step === 1 && (
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <h1 className="font-serif-display font-light tracking-tighter leading-[0.95] text-5xl md:text-6xl text-white mb-10">
            What kind of help <span className="italic">do you want today?</span>
          </h1>
          <button
            data-testid="intent-auto"
            onClick={pickAuto}
            className="group w-full flex items-center justify-between gap-5 bg-[#B5A58A] rounded-2xl p-7 mb-8 text-left hover:bg-[#c4b59a] transition-colors duration-300"
          >
            <div className="flex items-center gap-5">
              <Sparkles className="w-6 h-6 text-[#1F1E1B]/70" strokeWidth={1.5} />
              <div>
                <h3 className="font-serif-display font-light tracking-tight text-2xl text-[#1F1E1B]">Auto — pick for me</h3>
                <p className="text-sm text-[#1F1E1B]/70 mt-1">Not sure? We'll match the right advisor after you describe your situation.</p>
              </div>
            </div>
            <ArrowRight className="w-5 h-5 text-[#1F1E1B]/70 group-hover:translate-x-1 transition-transform duration-300" strokeWidth={1.5} />
          </button>

          <p className="font-mono-label text-[9px] tracking-[0.3em] uppercase text-stone-500 mb-4">Or pick an intent</p>
          <div className="space-y-3">
            {intents.map((i) => (
              <button
                key={i.id}
                data-testid={`intent-${i.id}`}
                onClick={() => pickIntent(i)}
                className="group w-full flex items-center justify-between bg-[#26241F] border border-white/[0.08] rounded-2xl px-6 py-5 hover:border-[#B5A58A]/40 transition-colors duration-300"
              >
                <span className="font-serif-display font-light tracking-tight text-xl text-white">{i.label}</span>
                <ArrowRight className="w-4 h-4 text-stone-500 group-hover:text-[#B5A58A] group-hover:translate-x-1 transition-all duration-300" strokeWidth={1.5} />
              </button>
            ))}
          </div>
        </motion.div>
      )}

      {step === 2 && advisor && advisor !== "auto" && (
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <h1 className="font-serif-display font-light tracking-tighter leading-[0.95] text-5xl md:text-6xl text-white mb-10">
            Your advisor <span className="italic">is seated.</span>
          </h1>
          <div className="bg-[#26241F] border border-[#B5A58A]/30 rounded-2xl p-8 mb-8" data-testid="advisor-preview">
            <p className="font-mono-label text-[9px] tracking-[0.25em] uppercase text-[#B5A58A]/80 mb-3">{advisor.category}</p>
            <h3 className="font-serif-display font-light tracking-tight text-4xl text-white mb-3">{advisor.name}</h3>
            <p className="text-sm leading-relaxed text-stone-400 mb-4">{advisor.philosophy}</p>
            <div className="flex gap-2 flex-wrap mb-4">
              {(advisor.traits || []).map((t) => (
                <span key={t} className="border border-white/[0.12] rounded-full px-3 py-1 font-mono-label text-[9px] tracking-[0.15em] uppercase text-stone-400">{t}</span>
              ))}
            </div>
            <p className="text-xs text-stone-500 leading-relaxed"><span className="text-stone-400">Best for:</span> {advisor.best_for}</p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <Btn testid="advisor-continue-btn" onClick={() => setStep(3)}>Continue with {advisor.name}</Btn>
            <Btn testid="advisor-back-btn" variant="outline" onClick={() => setStep(1)}>
              <ArrowLeft className="w-3.5 h-3.5 inline mr-1" strokeWidth={1.5} /> Change
            </Btn>
          </div>
        </motion.div>
      )}

      {step === 3 && (
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="relative">
          <h1 className="font-serif-display font-light tracking-tighter leading-[0.95] text-5xl md:text-6xl text-white mb-8">
            Describe the <span className="italic">situation.</span>
          </h1>

          {advisor === "auto" ? (
            <button
              data-testid="auto-chip"
              onClick={() => setStep(1)}
              className="inline-flex items-center gap-2 border border-[#B5A58A]/40 rounded-full px-4 py-2 font-mono-label text-[9px] tracking-[0.2em] uppercase text-[#B5A58A] mb-6 hover:border-[#B5A58A] transition-colors duration-300"
            >
              <Shuffle className="w-3.5 h-3.5" strokeWidth={1.5} />
              Auto pick — we'll match after you describe · Change
            </button>
          ) : advisor ? (
            <button
              data-testid="advisor-chip"
              onClick={() => setStep(2)}
              className="inline-flex items-center gap-2 border border-[#B5A58A]/40 rounded-full px-4 py-2 font-mono-label text-[9px] tracking-[0.2em] uppercase text-[#B5A58A] mb-6 hover:border-[#B5A58A] transition-colors duration-300"
            >
              {advisor.name} · Change
            </button>
          ) : null}

          <textarea
            data-testid="situation-input"
            value={situation}
            onChange={(e) => setSituation(e.target.value)}
            rows={6}
            maxLength={4000}
            placeholder="Lay it out plainly — what's happening, what's at stake, what you're torn about."
            className="w-full bg-[#26241F] border border-white/[0.08] rounded-2xl px-6 py-5 text-base leading-relaxed text-white placeholder:text-stone-500 outline-none focus:border-[#B5A58A]/50 transition-colors duration-300 resize-none mb-5"
          />

          <p className="font-mono-label text-[9px] tracking-[0.3em] uppercase text-stone-500 mb-3">Case type</p>
          <div className="flex gap-2 flex-wrap mb-6">
            {CASE_TYPES.map((t) => (
              <button
                key={t}
                data-testid={`case-type-${t}`}
                onClick={() => setCaseType(caseType === t ? "" : t)}
                className={`rounded-full px-4 py-2 font-mono-label text-[9px] tracking-[0.2em] uppercase border transition-colors duration-300 ${
                  caseType === t ? "border-[#B5A58A] text-[#B5A58A]" : "border-white/[0.12] text-stone-400 hover:text-white"
                }`}
              >
                {t}
              </button>
            ))}
          </div>

          <input
            data-testid="working-title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Working title (optional)"
            className="w-full bg-[#26241F] border border-white/[0.08] rounded-xl px-5 py-4 text-sm text-white placeholder:text-stone-500 outline-none focus:border-[#B5A58A]/50 transition-colors duration-300 mb-8"
          />

          <Btn testid="open-case-submit" onClick={submit} disabled={busy || situation.trim().length < 3}>
            {busy ? "Opening…" : "Open the case"}
          </Btn>
          {busy && <Spinner label="Seating your advisor…" />}

          <button
            data-testid="voice-mic-fab"
            onClick={recording ? stopRecording : startRecording}
            className={`fixed bottom-8 right-8 w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-colors duration-300 ${
              recording ? "bg-[#B37D3B] text-white animate-pulse" : "bg-[#B5A58A] text-[#1F1E1B] hover:bg-[#c4b59a]"
            }`}
          >
            {recording ? <Square className="w-5 h-5" strokeWidth={1.5} /> : <Mic className="w-5 h-5" strokeWidth={1.5} />}
          </button>
        </motion.div>
      )}
    </div>
  );
}
